import { useState, useEffect, useCallback, useMemo } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  FileText,
  DollarSign,
  ChevronLeft,
  ChevronRight,
  Eye,
  RefreshCw,
  Building2,
  Calendar,
  Hash,
  Filter,
  X,
  Download,
  FileCode,
  Search,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
} from "lucide-react";

// Status logic: Pagada, Activa, Cancelada
const getPaymentStatus = (inv) => {
  if (inv.Status === 2) return { key: "cancelada", label: "Cancelada", color: "bg-red-500" };
  const paid = inv.Payments || 0;
  const diff = (inv.Total || 0) - paid;
  if (inv.Total > 0 && diff < 1) return { key: "pagada", label: "Pagada", color: "bg-blue-500" };
  return { key: "activa", label: "Activa", color: "bg-emerald-500" };
};

const formatCurrency = (amount) => {
  if (!amount && amount !== 0) return "$0.00";
  return new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(amount);
};

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  return new Date(dateStr).toLocaleDateString("es-MX", { day: "2-digit", month: "short", year: "numeric" });
};

const EMPTY_FETCH_FILTERS = {
  client_name: "",
  date_from: "",
  date_to: "",
  status: "all",
  number_from: "",
  number_to: "",
};

const ERPFacturacion = () => {
  const { user } = useAuth();
  // Data
  const [allInvoices, setAllInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasSearched, setHasSearched] = useState(false);

  // Fetch filters (popup)
  const [showFetchDialog, setShowFetchDialog] = useState(false);
  const [fetchFilters, setFetchFilters] = useState({ ...EMPTY_FETCH_FILTERS });
  const [activeFetchFilters, setActiveFetchFilters] = useState({ ...EMPTY_FETCH_FILTERS });

  // Local filters (post-results)
  const [localSearch, setLocalSearch] = useState("");
  const [localStatuses, setLocalStatuses] = useState(new Set()); // empty = all
  const [localDateFrom, setLocalDateFrom] = useState("");
  const [localDateTo, setLocalDateTo] = useState("");
  const [localFiscal, setLocalFiscal] = useState("all");

  // Sort
  const [sortField, setSortField] = useState("Number");
  const [sortDir, setSortDir] = useState("desc");

  // Detail
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [invoiceDetail, setInvoiceDetail] = useState(null);
  const [showDetail, setShowDetail] = useState(false);

  const PAGE_SIZE = 100;

  const fetchInvoices = useCallback(async (filters, pageNum = 1) => {
    try {
      setLoading(true);
      const params = { page: pageNum, page_size: PAGE_SIZE };
      if (filters.client_name) params.client_name = filters.client_name;
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      if (filters.status && filters.status !== "all") params.status = filters.status;
      if (filters.number_from) params.number_from = filters.number_from;
      if (filters.number_to) params.number_to = filters.number_to;

      const res = await axios.get(`${API}/erp/invoices`, { params, withCredentials: true });
      const items = res.data.items || [];
      setAllInvoices(items);
      setHasSearched(true);
      setPage(pageNum);
      // Reset local filters on new fetch
      setLocalSearch("");
      setLocalStatuses(new Set());
      setLocalDateFrom("");
      setLocalDateTo("");
      setLocalFiscal("all");
    } catch (error) {
      toast.error("Error al cargar facturas de Bind ERP");
    } finally {
      setLoading(false);
    }
  }, []);

  const handleApplyFetchFilters = () => {
    setActiveFetchFilters({ ...fetchFilters });
    setShowFetchDialog(false);
    fetchInvoices(fetchFilters, 1);
  };

  const handleClearFetchFilters = () => {
    setFetchFilters({ ...EMPTY_FETCH_FILTERS });
    setActiveFetchFilters({ ...EMPTY_FETCH_FILTERS });
    setShowFetchDialog(false);
    fetchInvoices(EMPTY_FETCH_FILTERS, 1);
  };

  const openFetchDialog = () => {
    setFetchFilters({ ...activeFetchFilters });
    setShowFetchDialog(true);
  };

  const handlePageChange = (newPage) => {
    fetchInvoices(activeFetchFilters, newPage);
  };

  // Sort handler
  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDir("desc");
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown className="w-3 h-3 ml-1 inline opacity-30" />;
    return sortDir === "asc"
      ? <ArrowUp className="w-3 h-3 ml-1 inline text-blue-600" />
      : <ArrowDown className="w-3 h-3 ml-1 inline text-blue-600" />;
  };

  // Filtered + Sorted data
  const filteredInvoices = useMemo(() => {
    let result = [...allInvoices];

    // Local text search (client name, RFC)
    if (localSearch) {
      const q = localSearch.toLowerCase();
      result = result.filter(inv =>
        (inv.ClientName || "").toLowerCase().includes(q) ||
        (inv.RFC || "").toLowerCase().includes(q) ||
        String(inv.Number).includes(q)
      );
    }

    // Local status filter (multi-select)
    if (localStatuses.size > 0) {
      result = result.filter(inv => {
        const s = getPaymentStatus(inv).key;
        return localStatuses.has(s);
      });
    }

    // Local date filter
    if (localDateFrom) {
      const from = new Date(localDateFrom);
      result = result.filter(inv => new Date(inv.Date) >= from);
    }
    if (localDateTo) {
      const to = new Date(localDateTo + "T23:59:59");
      result = result.filter(inv => new Date(inv.Date) <= to);
    }

    // Fiscal filter
    if (localFiscal !== "all") {
      result = result.filter(inv => inv.IsFiscalInvoice === (localFiscal === "yes"));
    }

    // Sort
    result.sort((a, b) => {
      let valA = a[sortField];
      let valB = b[sortField];
      if (sortField === "Date" || sortField === "ExpirationDate") {
        valA = new Date(valA || 0).getTime();
        valB = new Date(valB || 0).getTime();
      }
      if (typeof valA === "string") valA = valA.toLowerCase();
      if (typeof valB === "string") valB = valB.toLowerCase();
      if (valA < valB) return sortDir === "asc" ? -1 : 1;
      if (valA > valB) return sortDir === "asc" ? 1 : -1;
      return 0;
    });

    return result;
  }, [allInvoices, localSearch, localStatuses, localDateFrom, localDateTo, localFiscal, sortField, sortDir]);

  // Stats from filtered results
  const stats = useMemo(() => {
    const totalAmt = filteredInvoices.reduce((acc, inv) => acc + (inv.Total || 0), 0);
    const pagadas = filteredInvoices.filter(inv => getPaymentStatus(inv).key === "pagada").length;
    const activas = filteredInvoices.filter(inv => getPaymentStatus(inv).key === "activa").length;
    const canceladas = filteredInvoices.filter(inv => getPaymentStatus(inv).key === "cancelada").length;
    return { total: filteredInvoices.length, totalAmount: totalAmt, pagadas, activas, canceladas };
  }, [filteredInvoices]);

  const fetchFilterCount = Object.entries(activeFetchFilters).filter(
    ([, v]) => v && v !== "all" && v !== ""
  ).length;

  const viewDetail = async (invoice) => {
    setSelectedInvoice(invoice);
    setShowDetail(true);
    setInvoiceDetail(null);
    try {
      const res = await axios.get(`${API}/erp/invoices/${invoice.ID}`, { withCredentials: true });
      setInvoiceDetail(res.data);
    } catch (error) {
      toast.error("Error al cargar detalle");
      setInvoiceDetail(null);
    }
  };

  const downloadFile = async (invoiceId, type, number) => {
    try {
      toast.info(`Descargando ${type.toUpperCase()}...`);
      const res = await axios.get(`${API}/erp/invoices/${invoiceId}/${type}`, { withCredentials: true });
      const { data: b64Data, type: mimeType } = res.data;

      const byteChars = atob(b64Data);
      const byteNumbers = new Array(byteChars.length);
      for (let i = 0; i < byteChars.length; i++) {
        byteNumbers[i] = byteChars.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: mimeType });
      const ext = type === "pdf" ? "pdf" : "xml";
      const fileName = `Factura_${number}.${ext}`;

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success(`${ext.toUpperCase()} descargado`);
    } catch (error) {
      console.error("Download error:", error);
      toast.error(`Error al descargar ${type.toUpperCase()}: ${error?.response?.data?.detail || error.message}`);
    }
  };

  return (
    <div className="p-4 md:p-6 lg:p-8" data-testid="erp-facturacion-page">
      <div className="max-w-7xl mx-auto space-y-5">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Facturación ERP</h1>
            <p className="text-slate-500">Consulta de facturas desde Bind ERP</p>
          </div>
          <Button onClick={openFetchDialog} className="bg-blue-600 hover:bg-blue-700 relative" data-testid="open-filter-dialog">
            <Filter className="w-4 h-4 mr-2" /> Consultar Facturas
            {fetchFilterCount > 0 && (
              <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                {fetchFilterCount}
              </span>
            )}
          </Button>
        </div>

        {/* Initial State */}
        {!hasSearched && !loading ? (
          <Card>
            <CardContent className="p-16 text-center">
              <Filter className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-600 mb-2">Consulta tus facturas de Bind ERP</h3>
              <p className="text-slate-400 mb-6">Haz clic en "Consultar Facturas" para buscar</p>
              <Button onClick={openFetchDialog} className="bg-blue-600 hover:bg-blue-700">
                <Filter className="w-4 h-4 mr-2" /> Consultar Facturas
              </Button>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <Card><CardContent className="p-3 flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg"><FileText className="w-4 h-4 text-blue-600" /></div>
                <div><p className="text-xl font-bold">{stats.total}</p><p className="text-xs text-slate-500">Resultados</p></div>
              </CardContent></Card>
              <Card><CardContent className="p-3 flex items-center gap-3">
                <div className="p-2 bg-emerald-100 rounded-lg"><DollarSign className="w-4 h-4 text-emerald-600" /></div>
                <div><p className="text-lg font-bold">{formatCurrency(stats.totalAmount)}</p><p className="text-xs text-slate-500">Total</p></div>
              </CardContent></Card>
              <Card><CardContent className="p-3 flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg"><DollarSign className="w-4 h-4 text-blue-600" /></div>
                <div><p className="text-xl font-bold">{stats.pagadas}</p><p className="text-xs text-slate-500">Pagadas</p></div>
              </CardContent></Card>
              <Card><CardContent className="p-3 flex items-center gap-3">
                <div className="p-2 bg-emerald-100 rounded-lg"><DollarSign className="w-4 h-4 text-emerald-600" /></div>
                <div><p className="text-xl font-bold">{stats.activas}</p><p className="text-xs text-slate-500">Activas</p></div>
              </CardContent></Card>
              <Card><CardContent className="p-3 flex items-center gap-3">
                <div className="p-2 bg-red-100 rounded-lg"><X className="w-4 h-4 text-red-600" /></div>
                <div><p className="text-xl font-bold">{stats.canceladas}</p><p className="text-xs text-slate-500">Canceladas</p></div>
              </CardContent></Card>
            </div>

            {/* Local Filters Bar */}
            <Card>
              <CardContent className="p-4">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
                  <div className="md:col-span-3">
                    <Label className="text-xs text-slate-500 mb-1 block">Buscar (Cliente, RFC, Folio)</Label>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        placeholder="Buscar en resultados..."
                        value={localSearch}
                        onChange={(e) => setLocalSearch(e.target.value)}
                        className="pl-10"
                        data-testid="local-search-input"
                      />
                    </div>
                  </div>
                  <div className="md:col-span-3">
                    <Label className="text-xs text-slate-500 mb-1 block">Estatus</Label>
                    <div className="flex gap-1 flex-wrap">
                      {[
                        { key: "pagada", label: "Pagada", color: "bg-blue-500" },
                        { key: "activa", label: "Activa", color: "bg-emerald-500" },
                        { key: "cancelada", label: "Cancelada", color: "bg-red-500" },
                      ].map(s => {
                        const isActive = localStatuses.has(s.key);
                        return (
                          <button
                            key={s.key}
                            type="button"
                            onClick={() => {
                              const next = new Set(localStatuses);
                              if (next.has(s.key)) next.delete(s.key); else next.add(s.key);
                              setLocalStatuses(next);
                            }}
                            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all border ${
                              isActive
                                ? `${s.color} text-white border-transparent`
                                : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
                            }`}
                            data-testid={`toggle-status-${s.key}`}
                          >
                            {s.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-xs text-slate-500 mb-1 block">Desde</Label>
                    <Input type="date" value={localDateFrom} onChange={(e) => setLocalDateFrom(e.target.value)} data-testid="local-date-from" />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-xs text-slate-500 mb-1 block">Hasta</Label>
                    <Input type="date" value={localDateTo} onChange={(e) => setLocalDateTo(e.target.value)} data-testid="local-date-to" />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-xs text-slate-500 mb-1 block">Fiscal</Label>
                    <Select value={localFiscal} onValueChange={setLocalFiscal}>
                      <SelectTrigger data-testid="local-fiscal-filter"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos</SelectItem>
                        <SelectItem value="yes">Timbrada</SelectItem>
                        <SelectItem value="no">Sin timbrar</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                {(localSearch || localStatuses.size > 0 || localDateFrom || localDateTo || localFiscal !== "all") && (
                  <div className="mt-3 flex items-center gap-2">
                    <span className="text-xs text-slate-400">Filtros locales activos</span>
                    <Button variant="ghost" size="sm" className="text-xs h-7" onClick={() => {
                      setLocalSearch(""); setLocalStatuses(new Set()); setLocalDateFrom(""); setLocalDateTo(""); setLocalFiscal("all");
                    }}>
                      <X className="w-3 h-3 mr-1" /> Limpiar
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Table */}
            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="cursor-pointer select-none" onClick={() => toggleSort("Number")}>
                        FOLIO <SortIcon field="Number" />
                      </TableHead>
                      <TableHead className="cursor-pointer select-none" onClick={() => toggleSort("Date")}>
                        FECHA <SortIcon field="Date" />
                      </TableHead>
                      <TableHead className="cursor-pointer select-none" onClick={() => toggleSort("ClientName")}>
                        CLIENTE <SortIcon field="ClientName" />
                      </TableHead>
                      <TableHead>RFC</TableHead>
                      <TableHead className="cursor-pointer select-none text-right" onClick={() => toggleSort("Subtotal")}>
                        SUBTOTAL <SortIcon field="Subtotal" />
                      </TableHead>
                      <TableHead className="text-right">IVA</TableHead>
                      <TableHead className="cursor-pointer select-none text-right" onClick={() => toggleSort("Total")}>
                        TOTAL <SortIcon field="Total" />
                      </TableHead>
                      <TableHead className="text-right">PAGADO</TableHead>
                      <TableHead>ESTATUS</TableHead>
                      <TableHead className="text-center">FISCAL</TableHead>
                      <TableHead className="text-right">VER</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={11} className="text-center py-10">
                          <RefreshCw className="w-5 h-5 animate-spin inline mr-2" /> Consultando Bind ERP...
                        </TableCell>
                      </TableRow>
                    ) : filteredInvoices.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={11} className="text-center py-10 text-slate-500">
                          No se encontraron facturas
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredInvoices.map((inv) => {
                        const payStatus = getPaymentStatus(inv);
                        const saldo = (inv.Total || 0) - (inv.Payments || 0);
                        return (
                          <TableRow key={inv.ID} className="hover:bg-slate-50 cursor-pointer" onClick={() => viewDetail(inv)}>
                            <TableCell className="font-mono font-bold">{inv.Number}</TableCell>
                            <TableCell className="whitespace-nowrap">{formatDate(inv.Date)}</TableCell>
                            <TableCell className="font-medium max-w-[180px] truncate">{inv.ClientName}</TableCell>
                            <TableCell className="font-mono text-xs">{inv.RFC}</TableCell>
                            <TableCell className="text-right">{formatCurrency(inv.Subtotal)}</TableCell>
                            <TableCell className="text-right">{formatCurrency(inv.VAT)}</TableCell>
                            <TableCell className="text-right font-bold">{formatCurrency(inv.Total)}</TableCell>
                            <TableCell className="text-right">{formatCurrency(inv.Payments)}</TableCell>
                            <TableCell>
                              <Badge className={`${payStatus.color} text-white text-xs whitespace-nowrap`}>{payStatus.label}</Badge>
                            </TableCell>
                            <TableCell className="text-center">
                              {inv.IsFiscalInvoice
                                ? <Badge variant="outline" className="text-xs border-green-400 text-green-700">Si</Badge>
                                : <Badge variant="outline" className="text-xs border-slate-300 text-slate-400">No</Badge>
                              }
                            </TableCell>
                            <TableCell className="text-right">
                              <Button size="icon" variant="ghost" onClick={(e) => { e.stopPropagation(); viewDetail(inv); }}>
                                <Eye className="w-4 h-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            {/* Pagination (for Bind API pages) */}
            {!loading && allInvoices.length > 0 && (
              <div className="flex items-center justify-between">
                <p className="text-sm text-slate-500">
                  {allInvoices.length} facturas cargadas de Bind (página {page})
                  {filteredInvoices.length !== allInvoices.length && ` — ${filteredInvoices.length} después de filtros locales`}
                </p>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => handlePageChange(page - 1)}>
                    <ChevronLeft className="w-4 h-4 mr-1" /> Anterior
                  </Button>
                  <Button variant="outline" size="sm" disabled={allInvoices.length < PAGE_SIZE} onClick={() => handlePageChange(page + 1)}>
                    Siguiente <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}

        {/* ============ FETCH FILTER DIALOG ============ */}
        <Dialog open={showFetchDialog} onOpenChange={setShowFetchDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Filter className="w-5 h-5 text-blue-600" />
                Consultar Facturas de Bind ERP
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-5">
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-slate-400" /> Nombre del Cliente
                </Label>
                <Input
                  placeholder="Buscar por nombre..."
                  value={fetchFilters.client_name}
                  onChange={(e) => setFetchFilters({ ...fetchFilters, client_name: e.target.value })}
                  data-testid="fetch-filter-client"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-slate-400" /> Fecha Desde
                  </Label>
                  <Input type="date" value={fetchFilters.date_from} onChange={(e) => setFetchFilters({ ...fetchFilters, date_from: e.target.value })} data-testid="fetch-filter-date-from" />
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-slate-400" /> Fecha Hasta
                  </Label>
                  <Input type="date" value={fetchFilters.date_to} onChange={(e) => setFetchFilters({ ...fetchFilters, date_to: e.target.value })} data-testid="fetch-filter-date-to" />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-slate-400" /> Estatus Bind
                </Label>
                <Select value={fetchFilters.status} onValueChange={(v) => setFetchFilters({ ...fetchFilters, status: v })}>
                  <SelectTrigger data-testid="fetch-filter-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="0">Borrador</SelectItem>
                    <SelectItem value="1">Vigente</SelectItem>
                    <SelectItem value="2">Cancelada</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Hash className="w-4 h-4 text-slate-400" /> Folio Desde
                  </Label>
                  <Input type="number" placeholder="Ej: 1000" value={fetchFilters.number_from} onChange={(e) => setFetchFilters({ ...fetchFilters, number_from: e.target.value })} data-testid="fetch-filter-num-from" />
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Hash className="w-4 h-4 text-slate-400" /> Folio Hasta
                  </Label>
                  <Input type="number" placeholder="Ej: 3500" value={fetchFilters.number_to} onChange={(e) => setFetchFilters({ ...fetchFilters, number_to: e.target.value })} data-testid="fetch-filter-num-to" />
                </div>
              </div>
            </div>
            <DialogFooter className="flex gap-2 mt-4">
              <Button variant="outline" onClick={handleClearFetchFilters} data-testid="clear-fetch-filters">Limpiar</Button>
              <Button onClick={handleApplyFetchFilters} className="bg-blue-600 hover:bg-blue-700" data-testid="apply-fetch-filters">
                <Search className="w-4 h-4 mr-2" /> Buscar Facturas
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ============ INVOICE DETAIL DIALOG ============ */}
        <Dialog open={showDetail} onOpenChange={setShowDetail}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Factura #{selectedInvoice?.Number}
              </DialogTitle>
              <div className="flex gap-2 pt-2">
                <Button size="sm" variant="outline" onClick={() => downloadFile(selectedInvoice?.ID, "pdf", selectedInvoice?.Number)} data-testid="download-pdf">
                  <Download className="w-4 h-4 mr-1" /> PDF
                </Button>
                <Button size="sm" variant="outline" onClick={() => downloadFile(selectedInvoice?.ID, "xml", selectedInvoice?.Number)} data-testid="download-xml">
                  <FileCode className="w-4 h-4 mr-1" /> XML
                </Button>
              </div>
            </DialogHeader>
            {invoiceDetail ? (
              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm text-slate-500"><Building2 className="w-4 h-4" /> Cliente</div>
                    <p className="font-semibold">{invoiceDetail.ClientName}</p>
                    <p className="text-sm text-slate-600">RFC: {invoiceDetail.RFC}</p>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm text-slate-500"><Calendar className="w-4 h-4" /> Fechas</div>
                    <p className="text-sm">Creación: {formatDate(invoiceDetail.CreationDate)}</p>
                    <p className="text-sm">Aplicación: {formatDate(invoiceDetail.ApplicationDate)}</p>
                    {invoiceDetail.Status && (
                      <Badge className={`${getPaymentStatus(invoiceDetail).color} text-white`}>{getPaymentStatus(invoiceDetail).label}</Badge>
                    )}
                  </div>
                </div>
                {invoiceDetail.FiscalID && (
                  <div className="bg-slate-50 p-3 rounded-lg">
                    <div className="flex items-center gap-2 text-sm text-slate-500 mb-1"><Hash className="w-4 h-4" /> Folio Fiscal (UUID)</div>
                    <p className="font-mono text-xs break-all">{invoiceDetail.FiscalID}</p>
                  </div>
                )}
                {invoiceDetail.Products?.length > 0 && (
                  <div>
                    <h3 className="font-semibold mb-2">Productos</h3>
                    <Table><TableHeader><TableRow>
                      <TableHead>Producto</TableHead><TableHead className="text-right">Cant.</TableHead><TableHead className="text-right">Precio</TableHead><TableHead className="text-right">Total</TableHead>
                    </TableRow></TableHeader><TableBody>
                      {invoiceDetail.Products.map((p, i) => (
                        <TableRow key={i}><TableCell>{p.ProductName || p.Name}</TableCell><TableCell className="text-right">{p.Quantity}</TableCell><TableCell className="text-right">{formatCurrency(p.Price)}</TableCell><TableCell className="text-right font-semibold">{formatCurrency(p.Total)}</TableCell></TableRow>
                      ))}
                    </TableBody></Table>
                  </div>
                )}
                {invoiceDetail.Services?.length > 0 && (
                  <div>
                    <h3 className="font-semibold mb-2">Servicios</h3>
                    <Table><TableHeader><TableRow>
                      <TableHead>Servicio</TableHead><TableHead className="text-right">Cant.</TableHead><TableHead className="text-right">Precio</TableHead><TableHead className="text-right">Total</TableHead>
                    </TableRow></TableHeader><TableBody>
                      {invoiceDetail.Services.map((s, i) => (
                        <TableRow key={i}><TableCell>{s.ServiceName || s.Name}</TableCell><TableCell className="text-right">{s.Quantity}</TableCell><TableCell className="text-right">{formatCurrency(s.Price)}</TableCell><TableCell className="text-right font-semibold">{formatCurrency(s.Total)}</TableCell></TableRow>
                      ))}
                    </TableBody></Table>
                  </div>
                )}
                <div className="bg-slate-50 p-4 rounded-lg space-y-2">
                  <div className="flex justify-between text-sm"><span>Subtotal</span><span>{formatCurrency(invoiceDetail.Subtotal)}</span></div>
                  {invoiceDetail.Discount > 0 && <div className="flex justify-between text-sm text-red-600"><span>Descuento</span><span>-{formatCurrency(invoiceDetail.Discount)}</span></div>}
                  <div className="flex justify-between text-sm"><span>IVA</span><span>{formatCurrency(invoiceDetail.VAT)}</span></div>
                  {invoiceDetail.VATRet > 0 && <div className="flex justify-between text-sm"><span>Ret. IVA</span><span>-{formatCurrency(invoiceDetail.VATRet)}</span></div>}
                  {invoiceDetail.ISRRet > 0 && <div className="flex justify-between text-sm"><span>Ret. ISR</span><span>-{formatCurrency(invoiceDetail.ISRRet)}</span></div>}
                  <div className="flex justify-between font-bold text-lg border-t pt-2"><span>Total</span><span>{formatCurrency(invoiceDetail.Subtotal - (invoiceDetail.Discount || 0) + (invoiceDetail.VAT || 0) - (invoiceDetail.VATRet || 0) - (invoiceDetail.ISRRet || 0))}</span></div>
                  {(invoiceDetail.Payments || 0) > 0 && <div className="flex justify-between text-sm text-emerald-600"><span>Pagos aplicados</span><span>{formatCurrency(invoiceDetail.Payments)}</span></div>}
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  {invoiceDetail.LocationName && <div><span className="text-slate-500">Sucursal:</span> {invoiceDetail.LocationName}</div>}
                  {invoiceDetail.CurrencyName && <div><span className="text-slate-500">Moneda:</span> {invoiceDetail.CurrencyName}</div>}
                  {invoiceDetail.CreatedByName && <div><span className="text-slate-500">Creado por:</span> {invoiceDetail.CreatedByName}</div>}
                  {invoiceDetail.CFDIPaymentMethod && <div><span className="text-slate-500">Método pago:</span> {invoiceDetail.CFDIPaymentMethod}</div>}
                </div>
              </div>
            ) : (
              <div className="text-center py-10"><RefreshCw className="w-5 h-5 animate-spin inline mr-2" /> Cargando detalle...</div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default ERPFacturacion;
